import { agentsApi, pollWhile } from '../../agents-api.ts';
import type { AgentInfo } from '../../agents-api.ts';
import { busy, button, confirmDialog, toast } from '../../components.ts';
import { icon } from '../../icons.ts';
import { ago, h } from '../../ui.ts';
import { branchLabel, questionBox, statusBadge } from './parts.ts';
import { detailsCard, previewCard, summaryCard } from './side.ts';
import { shotsCard } from './shots.ts';
import { liveCard } from './live.ts';

/** A part of the page rebuilt only when its signature changes, so typing and scrolling survive the poll. */
class Slot {
  readonly el = h('div', { class: 'slot' });
  private sig: string | null = null;
  set(sig: string, build: () => HTMLElement | null) {
    if (sig === this.sig) return;
    this.sig = sig;
    this.el.replaceChildren(...[build()].filter((x): x is HTMLElement => x !== null));
  }
}

/** One agent: what it is doing, its preview and question, and a line to tell it something (design-docs/0030). */
export async function agentView(id: string): Promise<HTMLElement> {
  const first = await agentsApi.get(id);
  const slots = {
    badge: new Slot(),
    actions: new Slot(),
    question: new Slot(),
    preview: new Slot(),
    summary: new Slot(),
    shots: new Slot(),
    live: new Slot(),
  };
  const timeline = h('ol', { class: 'timeline', 'aria-label': 'Activity', 'aria-live': 'polite' });
  const draw = (a: AgentInfo) => {
    slots.badge.set(a.status, () => statusBadge(a.status));
    slots.actions.set(a.status, () => stopButton(a, draw));
    slots.question.set(a.question?.id ?? '', () => (a.question ? questionCard(a, draw) : null));
    slots.preview.set(JSON.stringify(a.preview), () => previewCard(a, draw));
    slots.summary.set(a.summary ?? '', () => summaryCard(a));
    slots.shots.set(String(a.shots?.length ?? 0), () => shotsCard(a));
    slots.live.set(String(!!a.browser?.live), () => liveCard(a));
    drawTimeline(timeline, a);
  };
  const root = h(
    'div',
    { class: 'page agent-page' },
    h('a', { class: 'back', href: '#/agents' }, icon.back(14), 'Agents'),
    header(first, slots.badge.el, slots.actions.el),
    h(
      'div',
      { class: 'agent-grid' },
      h(
        'div',
        { class: 'stack' },
        slots.question.el,
        slots.live.el,
        slots.shots.el,
        h(
          'div',
          { class: 'card timeline-card' },
          h('div', { class: 'card-head' }, h('span', { class: 'label' }, 'Activity')),
          timeline,
        ),
        messageBox(first, draw),
      ),
      h('aside', { class: 'stack' }, slots.preview.el, slots.summary.el, detailsCard(first)),
    ),
  );
  draw(first);
  pollWhile(root, 3000, async () => draw(await agentsApi.get(id)));
  return root;
}

function header(a: AgentInfo, badge: HTMLElement, actions: HTMLElement): HTMLElement {
  return h(
    'header',
    { class: 'page-head' },
    h(
      'div',
      { class: 'grow' },
      h('div', { class: 'row' }, badge, h('span', { class: 'muted mono' }, `started ${ago(a.createdAt)}`)),
      h('h1', {}, a.title || a.task),
      h(
        'p',
        { class: 'page-sub row' },
        h('a', { class: 'link', href: `#/projects/${encodeURIComponent(a.project)}` }, icon.folder(14), a.project),
        branchLabel(a.branch),
      ),
    ),
    actions,
  );
}

function stopButton(a: AgentInfo, draw: (a: AgentInfo) => void): HTMLElement | null {
  if (a.status === 'stopped' || a.status === 'failed') return null;
  return button(
    [icon.stop(), 'Stop'],
    () =>
      confirmDialog(
        'Stop this agent?',
        `“${a.title}” stops now. Its branch and worktree stay; you can tell it to continue later.`,
        'Stop agent',
        async () => {
          draw(await agentsApi.stop(a.id));
          toast('Agent stopped');
        },
      ),
    { variant: 'outline', extra: 'danger-text' },
  );
}

function questionCard(a: AgentInfo, draw: (a: AgentInfo) => void): HTMLElement {
  return h(
    'div',
    { class: 'card warning' },
    h('span', { class: 'label warning-text' }, 'The agent asks'),
    questionBox(a, draw),
  );
}

/** The activity log, newest at the bottom; follows the end unless the owner scrolled up to read. */
function drawTimeline(list: HTMLElement, a: AgentInfo) {
  const sig = `${a.activity.length}:${a.activity.at(-1)?.at ?? 0}`;
  if (list.dataset.sig === sig) return;
  list.dataset.sig = sig;
  const atEnd = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
  const items = a.activity.map((x) =>
    h(
      'li',
      {},
      h(
        'time',
        { datetime: new Date(x.at).toISOString() },
        new Date(x.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      ),
      h('span', {}, x.text),
    ),
  );
  list.replaceChildren(...(items.length ? items : [h('li', { class: 'muted' }, h('span', {}, 'Nothing yet.'))]));
  if (atEnd || !list.dataset.seen) list.scrollTop = list.scrollHeight;
  list.dataset.seen = '1';
}

/** "Tell the agent…": new instructions in the same session (POST /agents/:id/message). */
function messageBox(a: AgentInfo, draw: (a: AgentInfo) => void): HTMLElement {
  const input = h('textarea', {
    class: 'textarea composer-input',
    rows: '2',
    placeholder: 'Tell the agent…',
    'aria-label': 'Message to the agent',
  });
  const send = button([icon.send(14), 'Send'], () => void go(), {});
  const go = () =>
    busy(send, async () => {
      const text = input.value.trim();
      if (!text) return input.focus();
      draw(await agentsApi.message(a.id, text));
      input.value = '';
      toast('Sent to the agent');
    });
  input.addEventListener('keydown', (e) => {
    const k = e as KeyboardEvent;
    if (k.key === 'Enter' && (k.metaKey || k.ctrlKey)) void go();
  });
  return h(
    'div',
    { class: 'composer' },
    input,
    h('div', { class: 'composer-foot' }, h('span', { class: 'muted mono' }, '⌘↵ to send'), send),
  );
}
